/**
 * <hero-facts>: the hero's facts row cycles between the static facts and the measured scale figures, in place.
 * Every version of a slot is already in the page, stacked in one grid cell, so a slot is always as tall as its
 * tallest version: rotating only swaps which layer is visible. The page never shifts.
 */

// Dependencies
import { registerComponent } from 'almostnojs';
import { Enhanced, reducedMotion } from './enhanced.js';

// How long each set shows, and the stagger between slots as a set changes.
const HOLD_MS = 5200, STAGGER_MS = 90;

/**
 * The rotating facts row.
 */
class HeroFacts extends Enhanced {

    /**
     * Start rotating (when there is more than one set) and keep the live figures current.
     *
     * @returns {void}
     */
    init() {

        // Slots and how many sets each holds.
        this.slots = [...this.querySelectorAll('.fact-stack')];
        this.sets = this.slots[0]?.querySelectorAll('.fact-layer').length ?? 0;
        this.index = 0;

        // Nothing to rotate.
        if (this.sets < 2) return;

        // Rotate unless motion is reduced, the row is hovered or focused, or the tab is hidden.
        const reduce = reducedMotion();
        const timer = setInterval(() => {
            if (reduce.matches || document.visibilityState !== 'visible' || this.matches(':hover, :focus-within')) return;
            this.show((this.index + 1) % this.sets);
        }, HOLD_MS);
        this.own(() => clearInterval(timer));
    }

    /**
     * Show one set, slot by slot.
     *
     * @param {number} target - Set index.
     * @returns {void}
     */
    show(target) {

        // Remember.
        this.index = target;

        // Swap each slot's visible layer, staggered left to right.
        this.slots.forEach((slot, k) => setTimeout(() => {
            slot.querySelectorAll('.fact-layer').forEach((layer, s) => {
                const on = s === target;
                layer.classList.toggle('is-active', on);
                if (on) layer.removeAttribute('aria-hidden');
                else layer.setAttribute('aria-hidden', 'true');
            });
        }, k * STAGGER_MS));
    }
}

// Register
registerComponent('hero-facts', HeroFacts);
